import Hls from 'hls.js'

export type VideoState = 'loading' | 'playing' | 'blocked' | 'failed'

export interface VideoUrls {
  hls: string
  mp4Fallback: string
  poster: string
}

export interface AutoplayResult {
  state: VideoState
  error?: string
}

export function handleVideoAutoplay(
  video: HTMLVideoElement,
  videoUrls: VideoUrls,
  onStateChange: (result: AutoplayResult) => void,
  hlsRef?: { current: Hls | null },
): () => void {
  let isDestroyed = false

  const setState = (state: VideoState, error?: string) => {
    if (!isDestroyed) {
      onStateChange({ state, error })
    }
  }

  const handleCanPlay = () => {
    if (isDestroyed) return

    setState('loading')

    video
      .play()
      .then(() => setState('playing'))
      .catch((error) => {
        setState('blocked', error.message)
      })
  }

  const handleError = (error?: string) => {
    setState('failed', error || 'Video failed to load')
  }

  const handleHlsError = (_: any, data: any) => {
    if (data.fatal && !isDestroyed) {
      handleError(`HLS Error: ${data.type} - ${data.details}`)
    }
  }

  setState('loading')

  if (video.canPlayType('application/vnd.apple.mpegurl')) {
    video.src = videoUrls.hls
    video.addEventListener('canplay', handleCanPlay)
  } else if (Hls.isSupported()) {
    const hls = new Hls({
      enableWorker: true,
      lowLatencyMode: true,
      maxBufferLength: 30,
    })

    if (hlsRef) {
      hlsRef.current = hls
    }

    hls.loadSource(videoUrls.hls)
    hls.attachMedia(video)
    hls.on(Hls.Events.MANIFEST_PARSED, handleCanPlay)
    hls.on(Hls.Events.ERROR, handleHlsError)
  } else {
    video.src = videoUrls.mp4Fallback
    video.addEventListener('canplay', handleCanPlay)
  }

  video.addEventListener('error', () => handleError())

  return () => {
    isDestroyed = true
    video.removeEventListener('canplay', handleCanPlay)
    video.removeEventListener('error', () => handleError())
    if (hlsRef?.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }
  }
}
